import * as React from 'react';
import { Component, Fragment } from 'react';
import { observer, inject } from 'mobx-react';
import { action, observable, computed } from 'mobx';
import styled, { withTheme } from 'styled-components';
import { propStyles } from '@kurtosys/ksys-portal-components/build/utils/propStyles';
import { top } from '../utils/customPropStyles';
import Chevron from './Chevron';
import Translate from './Translate';
import ClickLayer from './ClickLayer';
import SortToggle from './SortToggle';
import getSortKeyFromColumn from "../utils/getSortKeyFromColumn";
import isExistingAdvancedSort from "../utils/isExistingAdvancedSort";
import get from 'lodash/get';
import { DataStore } from '../stores';
import { SORT_SELECTOR_PLACEHOLDER } from '../constants';

const SortSelectorWrapper = styled<any, any>('div')`
    ${propStyles};
`;

SortSelectorWrapper.defaultProps = {
    flex: "1 0 100%",
    paddingY: "sm",
    bgColor: "white"
};

const SortSelectorInnerWrapper = styled<any, any>('div')`
    ${propStyles};
`;

SortSelectorInnerWrapper.defaultProps = {
    position: "relative",
    display: "flex"
};

const SortSelectorDropdownButton = styled<any, any>('button')`
    ${propStyles};
    outline: none;
    cursor: pointer;
`;

SortSelectorDropdownButton.defaultProps = {
    display: "flex",
    flex: "1 1 auto",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
    overflow: "hidden",
    padding: "sm",
    fontSize: "sm",
    fontFamily: "body",
    fontWeight: "normal",
    color: "body",
    bgColor: "white",
    borderWidth: "1px",
    borderColor: "gray300",
    borderStyle: "solid",
    borderRadius: "sm",
    hover: {
        borderColor: "gray500"
    },
    selected: {
        color: "body",
        borderColor: "gray500"
    }
};

const SortSelectorDropdownText = styled<any, any>('div')`
    ${propStyles};
    white-space: nowrap;
`;

SortSelectorDropdownText.defaultProps = {
    textAlign: "left",
    overflow: "hidden",
    textOverflow: "ellipsis",
    color: "inherit"
};

const SortSelectorDropdownMenu = styled<any, any>('div')`
    ${propStyles};
    ${top};
`;

SortSelectorDropdownMenu.defaultProps = {
    position: "absolute",
    top: "calc(100% + 1px)",
    zIndex: "dropdown",
    width: ["100%", "80%"],
    margin: ["0", "0 10%"],
    bgColor: "white",
    borderWidth: "1px",
    borderColor: "gray300",
    borderStyle: "solid",
    borderRadius: "sm",
    boxShadow: "sm"
};

const SortSelectorDropdownList = styled<any, any>('ol')`
    ${propStyles};
    list-style: none;
    -ms-overflow-style: -ms-autohiding-scrollbar;
`;

SortSelectorDropdownList.defaultProps = {
    maxHeight: "300px",
    overflowX: "hidden",
    overflowY: "auto",
};

const SortSelectorDropdownListItem = styled<any, any>('li')`
    ${propStyles};
`;

SortSelectorDropdownListItem.defaultProps = {
    fontFamily: "body",
    fontWeight: "normal",
    paddingY: "sm",
    paddingX: "md",
    borderWidth: "1px",
    borderStyle: "none none solid none",
    borderColor: "gray300",
    cursor: "pointer",
    alternated: {
        bgColor: "gray100"
    },
    hover: {
        bgColor: "gray100"
    },
    selected: {
        color: "primary",
        fontWeight: "bold"
    }
};

interface SortSelectorProps {
    theme?: { [property: string]: string | number };
    dataStore?: DataStore;
    order?: string;
}

@inject('dataStore')
@observer
class SortSelector extends Component<SortSelectorProps> {
    @observable isOpen = false;
    @observable selectedColumnIndex = -1;

    @action
    toggleOpen = () => {
        this.isOpen = !this.isOpen;
    }

    @action
    selectSortColumn = (column, index) => {
        this.selectedColumnIndex = index;
        this.isOpen = false;
        this.props.dataStore.toggleSort(column);
    }

    @computed
    get selectedColumn() {
        return this.props.dataStore.sortableColumns[this.selectedColumnIndex];
    }

    @computed
    get direction() {
        let direction = undefined;

        if (this.selectedColumn) {
            const key = getSortKeyFromColumn(this.selectedColumn);

            if (this.props.dataStore.advancedSort && isExistingAdvancedSort(this.selectedColumn, this.props.dataStore.advancedSort)) {
                direction = get(this.props.dataStore, 'advancedSort.direction', '');
            } else {
                direction = get(this.props.dataStore.propertySort.find(c => c.key === key), 'direction', '');
            }
        }

        return direction;
    }

    render() {
        const {
            props: { theme, dataStore, ...props },
            isOpen, selectedColumnIndex, toggleOpen, selectedColumn, selectSortColumn, direction
        } = this;

        return (
            <SortSelectorWrapper
                {...props}
                {...theme.SortSelectorWrapper}
            >
                <SortSelectorInnerWrapper
                    {...theme.SortSelectorInnerWrapper}
                >
                    <SortSelectorDropdownButton
                        onClick={toggleOpen}
                        data-selected={isOpen}
                        {...theme.SortSelectorDropdownButton}
                    >
                        <SortSelectorDropdownText
                            {...theme.SortSelectorDropdownText}
                        >
                            <Translate>{selectedColumn ? selectedColumn.label : SORT_SELECTOR_PLACEHOLDER}</Translate>
                        </SortSelectorDropdownText>
                        <Chevron
                            fileName="ColumnDropdownChevronIcon"
                            direction={isOpen ? 'up' : 'down'}
                            {...theme.SortSelectorDropdownChevron}
                        />
                    </SortSelectorDropdownButton>
                    <SortToggle
                        direction={direction}
                        onClick={() => dataStore.toggleSort(selectedColumn)}
                        disabled={!selectedColumn}
                        paddingY="sm"
                        paddingX="md"
                        margin="0 0 0 10px"
                        color="gray500"
                        bgColor="gray100"
                        borderWidth="1px"
                        borderColor="gray300"
                        borderStyle="solid"
                        borderRadius="sm"
                        boxShadow="sm"
                        hover={{ borderColor: "gray500" }}
                        {...theme.SortSelectorSortToggle}
                    />
                    {
                        isOpen && (
                            <Fragment>
                                <ClickLayer
                                    onClick={toggleOpen}
                                />
                                <SortSelectorDropdownMenu
                                    {...theme.SortSelectorDropdownMenu}
                                >
                                    <SortSelectorDropdownList
                                        {...theme.SortSelectorDropdownList}
                                    >
                                        {
                                            dataStore.sortableColumns.map((column, i) =>
                                                <SortSelectorDropdownListItem
                                                    key={i}
                                                    onClick={() => selectSortColumn(column, i)}
                                                    data-alternated={i % 2 ? true : false}
                                                    data-selected={i === selectedColumnIndex}
                                                    {...theme.SortSelectorDropdownListItem}
                                                >
                                                    <Translate>{column.label}</Translate>
                                                </SortSelectorDropdownListItem>
                                            )
                                        }
                                    </SortSelectorDropdownList>
                                </SortSelectorDropdownMenu>
                            </Fragment>
                        )
                    }
                </SortSelectorInnerWrapper>
            </SortSelectorWrapper>
        );
    }
}

export default withTheme(SortSelector);


// WEBPACK FOOTER //
// ./src/Components/SortSelector.tsx